import { z } from "zod";

export const RequestKitInitSchema = z.object({
  baseURL: z
    .string({
      invalid_type_error: "baseURL must be a string",
    })
    .url({
      message: "baseURL must be a valid URL",
    })
    .optional(),
  apiKey: z.string({
    invalid_type_error: "apiKey must be a string",
    required_error: "apiKey is required",
  }),
  retries: z.number().optional(),
  logging: z
    .object({
      enabled: z.coerce.boolean().default(false).optional(),
    })
    .optional(),
});

// Request schemas
export const RequestSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  createdAt: z.string(),
  upvotes: z.number(),
  comments: z.number(),
  isUpvoted: z.boolean().optional(),
  status: z
    .object({
      name: z.string(),
      color: z.string(),
      description: z.string().optional(),
      disableComments: z.boolean(),
      isPublic: z.boolean(),
    })
    .optional(),
  requester: z
    .object({
      email: z.string().email().optional(),
      name: z.string().optional(),
      identifier: z.string(),
    })
    .optional(),
});

export const CommentSchema = z.object({
  id: z.string(),
  content: z.string(),
  createdAt: z.string(),
  commentor: z.object({
    email: z.string().email().optional(),
    identifier: z.string(),
    name: z.string().optional(),
  }),
});

// API Schemas
export const GetRequestsQuerySchema = z.object({
  limit: z.number().min(1).max(100).default(10).optional(),
  page: z.number().min(1).default(1).optional(),
  includeRequester: z.boolean().default(false).optional(),
  orderBy: z.array(z.enum(["date", "upvotes"])).default(["date"]).optional(),
  order: z.enum(["asc", "desc"]).default("desc").optional(),
  userIdentifier: z.string().optional(),
});

export const GetRequestsOutputSchema = z.object({
  totalRequests: z.number(),
  requests: z.array(RequestSchema),
});

export const CreateRequestInputSchema = z.object({
  title: z.string({
    invalid_type_error: "title must be a string",
    required_error: "title is required",
  }),
  description: z.string({
    invalid_type_error: "description must be a string",
    required_error: "description is required",
  }),
  status: z.string().optional(),
  requester: z
    .object({
      identifier: z.string({
        invalid_type_error: "identifier must be a string",
        required_error: "identifier is required",
      }),
      name: z.string().optional(),
      email: z.string().email().optional(),
    })
    .optional(),
  files: z.union([
    z.array(z.instanceof(Blob)),
    z.instanceof(Blob).transform((blob) => [blob])
  ]).optional(),
});

export const CreateRequestOutputSchema = z.object({
  id: z.string(),
});

export const UpvoteRequestInputSchema = z.object({
  user: z
    .object({
      identifier: z.string(),
      name: z.string().optional(),
      email: z.string().email().optional(),
    })
    .optional(),
});

export const UpvoteRequestOutputSchema = z.object({
  id: z.string(),
});

export const RemoveUpvoteQuerySchema = z.object({
  userIdentifier: z.string(),
});

export const GetRequestQuerySchema = z.object({
  includeRequester: z.boolean().default(false).optional(),
  includeComments: z.boolean().default(false).optional(),
  userIdentifier: z.string().optional(),
});

export const GetCommentsQuerySchema = z.object({
  limit: z.number().min(1).max(100).default(10).optional(),
  page: z.number().min(1).default(1).optional(),
});

export const GetCommentsOutputSchema = z.object({
  comments: z.array(CommentSchema),
});

export const CreateCommentInputSchema = z.object({
  content: z.string({
    invalid_type_error: "content must be a string",
    required_error: "content is required",
  }),
  commentor: z.object({
    identifier: z.string({
      invalid_type_error: "identifier must be a string",
      required_error: "identifier is required",
    }),
    name: z.string().optional(),
    email: z.string().email().optional(),
  }),
  files: z.array(z.instanceof(Blob)).optional(),
});

export const CreateCommentOutputSchema = z.object({
  id: z.string(),
});

export const DeleteCommentBodySchema = z.object({
  userIdentifier: z.string(),
});

// Error response schema
export const ErrorResponseSchema = z.object({
  message: z.string(),
  code: z.string().optional(),
  details: z.record(z.unknown()).optional(),
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// Export a type for the API schema
export type RequestKitSchema = {
  "@get/requests": {
    query: z.infer<typeof GetRequestsQuerySchema>;
    output: z.infer<typeof GetRequestsOutputSchema>;
  };
  "@post/requests": {
    input: z.infer<typeof CreateRequestInputSchema>;
    output: z.infer<typeof CreateRequestOutputSchema>;
  };
  "@post/requests/:id/upvote": {
    params: { id: string };
    input: z.infer<typeof UpvoteRequestInputSchema>;
    output: z.infer<typeof UpvoteRequestOutputSchema>;
  };
  "@delete/requests/:id/upvote": {
    params: { id: string };
    query: z.infer<typeof RemoveUpvoteQuerySchema>;
    output: z.infer<typeof UpvoteRequestOutputSchema>;
  };
  "@get/requests/:id": {
    params: { id: string };
    query: z.infer<typeof GetRequestQuerySchema>;
    output: z.infer<typeof RequestSchema>;
  };
  "@get/requests/:id/comments": {
    params: { id: string };
    query: z.infer<typeof GetCommentsQuerySchema>;
    output: z.infer<typeof GetCommentsOutputSchema>;
  };
  "@post/requests/:id/comments": {
    params: { id: string };
    input: z.infer<typeof CreateCommentInputSchema>;
    output: z.infer<typeof CreateCommentOutputSchema>;
  };
  "@delete/requests/:id/comments/:commentId": {
    params: { id: string; commentId: string };
    body: z.infer<typeof DeleteCommentBodySchema>;
    output: z.infer<typeof CreateCommentOutputSchema>;
  };
};
